import { useTranslation } from 'react-i18next';
import type { ReachPoint } from '@up2d8/contracts';

/** Eén week in de grafiek: maandag als datum, en de ontvangers van die zeven dagen samen. */
export interface ReachWeek {
  /** De maandag, `YYYY-MM-DD`. */
  week: string;
  recipients: number;
  seen: number;
}

/**
 * De dagreeks van de server, per week samengeteld (maandag tot zondag).
 *
 * De server rekent per dag; de mockup toont weken. Per dag zijn het er 90 of 365, en een lijn met
 * zoveel punten is ruis: één bericht op maandag geeft elke week een piek en een dal.
 */
export function weeksOf(series: ReachPoint[]): ReachWeek[] {
  const weeks = new Map<string, ReachWeek>();
  for (const point of series) {
    const date = new Date(`${point.date}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) continue;
    // getUTCDay: zondag = 0. Terug naar de maandag van die week.
    date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
    const week = date.toISOString().slice(0, 10);
    const current = weeks.get(week) ?? { week, recipients: 0, seen: 0 };
    current.recipients += point.recipients;
    current.seen += point.seen;
    weeks.set(week, current);
  }
  return [...weeks.values()].sort((a, b) => a.week.localeCompare(b.week));
}

/** Een rond getal voor de stap tussen de rasterlijnen: 1, 2, 5, 10, 20, 25, 50, … */
function niceStep(max: number): number {
  const raw = Math.max(1, max) / 4;
  const power = 10 ** Math.floor(Math.log10(raw));
  for (const factor of [1, 2, 2.5, 5, 10]) {
    if (factor * power >= raw) return Math.max(1, Math.ceil(factor * power));
  }
  return Math.ceil(10 * power);
}

/** "14/09" uit `2026-09-14`. */
function dayMonth(week: string): string {
  return `${week.slice(8, 10)}/${week.slice(5, 7)}`;
}

// De maten van de mockup (SVG 820 × 240): het plotvlak loopt van x 44 tot 804 en van y 16 tot 206.
const LEFT = 44;
const RIGHT = 804;
const TOP = 16;
const BOTTOM = 206;

/**
 * "Bereik per week" (V1): verstuurd als vlak met een lijn, gezien als donkere lijn; het laatste
 * punt van elke lijn is een stip met zijn getal.
 *
 * Met de hand getekend, zoals de staven ervoor: geen grafiekbibliotheek voor twee lijnen, op een
 * bundel die ook op een fabriekstelefoon binnenkomt. De vorm, kleuren en maten zijn die van de
 * mockup; alleen de schaal van de y-as volgt de gegevens (vier gelijke stappen vanaf 0).
 */
export function ReachChart({ weeks }: { weeks: ReachWeek[] }) {
  const { t } = useTranslation();
  const last = weeks[weeks.length - 1];
  if (!last) {
    return <p className="a-muted a-small">{t('reports.emptyTitle')}</p>;
  }

  const step = niceStep(Math.max(...weeks.map((week) => week.recipients)));
  const top = step * 4;
  const y = (value: number) => BOTTOM - (value / top) * (BOTTOM - TOP);
  const x = (index: number) =>
    weeks.length === 1 ? RIGHT : LEFT + (index * (RIGHT - LEFT)) / (weeks.length - 1);
  const point = (index: number, value: number) => `${x(index).toFixed(1)},${y(value).toFixed(1)}`;

  const sent = weeks.map((week, index) => point(index, week.recipients));
  const seen = weeks.map((week, index) => point(index, week.seen));
  const area = `M${x(0).toFixed(1)},${BOTTOM.toFixed(1)} L${sent.join(' L')} L${x(weeks.length - 1).toFixed(1)},${BOTTOM.toFixed(1)} Z`;
  // Zeven datums op de x-as, zoals de mockup bij dertien weken (om de twee weken).
  const labelEvery = Math.max(1, Math.ceil(weeks.length / 7));
  const lastIndex = weeks.length - 1;
  // De laatste week krijgt altijd haar datum (ze staat ook in het aria-label); een datum vlak
  // ervoor valt weg, anders schuiven de twee over elkaar.
  const showLabel = (index: number) =>
    index === lastIndex ||
    (index % labelEvery === 0 && lastIndex - index >= Math.ceil(labelEvery / 2));
  const lastX = x(lastIndex);

  return (
    <svg
      className="a-chart"
      viewBox="0 0 820 240"
      role="img"
      data-testid="reach-chart"
      aria-label={t('beheer.opvolging.reports.chartLabel', {
        week: dayMonth(last.week),
        sent: last.recipients,
        seen: last.seen,
      })}
    >
      {[0, 1, 2, 3, 4].map((index) => {
        const value = step * index;
        const lineY = y(value);
        return (
          <g key={index}>
            <line x1={LEFT} x2={RIGHT} y1={lineY} y2={lineY} stroke="#EEF1F5" />
            <text
              x={36}
              y={lineY + 4}
              textAnchor="end"
              fontSize={11}
              style={{ fill: 'var(--a-subtle)' }}
            >
              {value}
            </text>
          </g>
        );
      })}
      {weeks.map((week, index) =>
        showLabel(index) ? (
          <text
            key={week.week}
            x={x(index)}
            y={228}
            textAnchor="middle"
            fontSize={11}
            style={{ fill: 'var(--a-subtle)' }}
          >
            {dayMonth(week.week)}
          </text>
        ) : null,
      )}
      <path d={area} style={{ fill: 'var(--brand-primary-tint)' }} />
      <polyline
        points={sent.join(' ')}
        fill="none"
        style={{ stroke: 'var(--brand-primary)' }}
        strokeWidth={2}
      />
      <polyline
        points={seen.join(' ')}
        fill="none"
        style={{ stroke: 'var(--brand-dark-blue)' }}
        strokeWidth={2.5}
      />
      <circle cx={lastX} cy={y(last.recipients)} r={4} style={{ fill: 'var(--brand-primary)' }} />
      <circle cx={lastX} cy={y(last.seen)} r={4.5} style={{ fill: 'var(--brand-dark-blue)' }} />
      <text
        x={lastX - 8}
        y={Math.max(12, y(last.recipients) - 8)}
        textAnchor="end"
        fontSize={12}
        fontWeight={600}
        style={{ fill: 'var(--a-primary)' }}
      >
        {t('beheer.opvolging.reports.lastSent', { count: last.recipients })}
      </text>
      <text
        x={lastX - 8}
        y={Math.min(BOTTOM - 6, y(last.seen) + 22)}
        textAnchor="end"
        fontSize={12}
        fontWeight={600}
        style={{ fill: 'var(--brand-dark-blue)' }}
      >
        {t('beheer.opvolging.reports.lastSeen', { count: last.seen })}
      </text>
    </svg>
  );
}
